import { ParsedCommsItem } from "@app/modules/dashboard/components/comms/entities";
import { ChildOrder, OrderUpdate } from "@app/modules/orders/entities/orders.entities";
import { Report } from "@app/modules/reports/entities";
import { PERMISSION } from "@app/modules/roles/constants";

export type GlobalState = {
  comms: {
    announcements: ParsedCommsItem[];
    header: ParsedCommsItem | null;
    notifications: ParsedCommsItem[];
  };
  reports: Report[];
  onboarding: {
    hasOrders: boolean;
    hasPickingPoints: boolean;
  };
  settlement: {
    isCashless: boolean;
  };
  kustomerJWT: string | undefined;
  serializedPickingPoints: SerializedPickingPoint[];
  dragCoords: DragData;
  checkin: CheckIn;
  ui: UiGlobal;
};

export enum RetailerOrigin {
  CARGO = "CARGO",
  TU_WEB = "TU_WEB",
  RAPPI_ALIADOS = "RAPPI_ALIADOS",
  BOX_DELIVERY = "BOX_DELIVERY",
  IFOOD = "IFOOD",
}

export interface BasicRetailer {
  id: number;
  name: string;
  permissions: PERMISSION[];
  role: string;
  enable_order_approval?: boolean;
}

export interface Retailer {
  id: number;
  multiple_action_points_enabled: boolean;
  delivery_handshake_value: number;
  collect_cash_enabled: boolean;
  return_enabled: boolean;
  default_tip: number | null;
  flow_type: string;
  default_callback: string;
  primary_color: string;
  has_logo: boolean;
  name: string;
  email: string;
  send_tracking_sms: boolean;
  send_tracking_email: boolean;
  checkin_enabled: boolean;
  origin: RetailerOrigin;
  callback_header: string;
  pos_terminal_enabled: boolean;
  callback_header_value: string;
  return_min_value: number | null;
  inverse_orders_enabled: boolean;
  requires_photo: boolean;
  send_storekeeper_updates: boolean;
  tracking_link_sending_status_id: number;
  min_semaphore_for_default_tips: number | null;
  general_default_tip: number | null;
  allow_cooking_time_edit: boolean | null;
  is_cashless?: boolean;
}

export interface CheckInData {
  id: number;
  code: string;
  store_id: number;
  created_at: string;
  expire_at: string;
  updated_at: string;
}

export interface CheckIn {
  checkin: CheckInData;
  name: string;
}

export interface UiGlobal {
  loadPickingPoints?: boolean;
  loadingCheckin?: boolean;
  failedCheckin?: boolean;
  loadingRetailer?: boolean;
  loadingReport?: boolean;
  loadingOnboarding?: boolean;
  visibleHeader?: boolean;
  openReportsWindow?: boolean;
  openRolesDialogModal?: boolean;
  showReportsWindowButton?: boolean;
}

export interface IError {
  id: string;
  message: string;
}

export interface DragData {
  coords: { lat: number; lng: number };
  address: string;
}

export interface ProgressType {
  retail_id: number;
  legal_representative: boolean;
  business_information: boolean;
  contract_signing: boolean;
}

export interface PickingPoint {
  address: string;
  checkin_enabled?: boolean;
  city: string;
  contact_email?: string;
  closed_by_reports?: boolean;
  contact_name?: string;
  default_tip?: number;
  external_id?: string;
  general_default_tip: number | null;
  handoff_enabled?: boolean;
  handshake_enabled?: boolean;
  id?: number;
  lat: number;
  lng: number;
  meeting_point_image_url?: string;
  min_semaphore_for_default_tips: number | null;
  name: string;
  phone: string;
  pickup_instructions?: string;
  preparation_time?: number;
  product_return_instructions?: string;
  rappi_store_id?: number;
  retailer_id?: number;
  return_enabled?: boolean;
  scheduled_reopen_time?: string;
  status?: number;
  zip_code?: string;
  billing_rappi_store_id?: number;
  cargo_rappi_store_id?: number;
  uses_cargo_rappi_store?: boolean;
  ifood_handshake_enabled?: boolean;
  pos_terminal_handshake_enabled?: boolean;
}

export interface SerializedPickingPoint {
  label: string;
  key: PickingPoint;
}

export interface DropDownTypes<T, E> {
  key: T;
  label: E;
  subtitle?: string;
}

export interface FormEmitter<T> {
  key?: string;
  form_status: boolean;
  data: T;
}

export interface DropOff {
  address: string;
  city: string;
  lat?: number;
  lng?: number;
  place_id?: string;
  zip_code: string;
  complement?: string;
}

export enum AlertSeverity {
  "INFO" = "info",
  "WARNING" = "warning",
  "ERROR" = "error",
  "SUCCESS" = "success",
}

export enum TransportMediaType {
  neither = "neither",
  bicycle = "bicycle",
  motorbike = "motorbike",
  car = "car",
  generic = "generic",
}

export type OrderStatusResDTOV3 = {
  additional_data: any;
  address: string;
  action_points: any;
  agent_id: any | null;
  brand_id: number;
  bundle_has_multiple_store_ids: any | null;
  calculated_tip: string;
  callback: any | null;
  child_orders: ChildOrder[];
  parent_order_id: number;
  cancel_failed_delivery_task_active: any | null;
  cancel_failed_delivery_task_id: any | null;
  cancelation_reason: string;
  cancelation_reason_core: any | null;
  cancellation_requested_by: any | null;
  cancellation_user_id: any | null;
  cancelled_at: string;
  check_order_task_active: any | null;
  check_order_task_id: any | null;
  city: string;
  collect_cash: number;
  core_cancel_reason_id: any | null;
  created_at: string;
  crossdock_operation_id: any | null;
  darkstore_id: any | null;
  deadline_at: string;
  destination_phone_code: string | null;
  debt_cancelled_at: any | null;
  debt_cancelled_by: any | null;
  default_tip: string;
  delivered_at: any | null;
  delivery_assigned_at: any | null;
  in_store_reference_id: string | null;
  delivery_type_id: number;
  deliveryman_type: any | null;
  destination_address: string;
  destination_city: string;
  destination_comments: any | null;
  destination_description: string;
  destination_email: string;
  destination_external_id: any | null;
  destination_first_name: string;
  destination_google_place_id: any | null;
  destination_last_name: string;
  destination_lat: number;
  destination_lng: number;
  destination_phone: string;
  destination_zip_code: any | null;
  dispatch_group_id: any | null;
  dispatch_group_priority: number;
  estimated_price: any | null;
  estimated_tax_base_price: any | null;
  eta: number;
  external_order_id: string;
  final_delivery_comments: any | null;
  final_delivery_lat: any | null;
  final_delivery_lng: any | null;
  final_delivery_point_image: any | null;
  flow: any | null;
  handoff_code: any | null;
  handoff_qr: any | null;
  handshake_code: any | null;
  handshake_response: any | null;
  high_demand_charge: any | null;
  id: string;
  instaleap_id: any | null;
  integration_external_id: any | null;
  is_active: boolean;
  last_status_core: string;
  lat: number;
  lng: number;
  minutes_since_last_update: number | null;
  needs_picking: any | null;
  order_status_id: number;
  order_type_id: number;
  order_update: OrderUpdate[];
  orders_in_bundle: any | null;
  origin: any | null;
  origin_detail: any | null;
  partner_cancelation_reason: any | null;
  partner_cancelation_reason_other: any | null;
  partner_channel: any | null;
  payment_method: any | null;
  phone: string;
  // esto se hizo para poder poner atributos nested en el selection set
  // sin afectar los tipos ActiveOrderListResponse y ArchivedOrderListResponse
  "picking_point.name"?: string;
  picking_point: any;
  picking_point_id: number;
  picking_point_rappi_store_id: any | null;
  placed_at: any | null;
  pp_cargo_rappi_store_id: any | null;
  pp_rappi_store_id: any | null;
  preparation_time: any | null;
  raia_extended_order_id: any | null;
  raia_state_code: any | null;
  raia_tracking_reference: any | null;
  raia_transportation_order_id: any | null;
  rain_charge: any | null;
  rappi_order_created_at: any | null;
  rappi_order_id: string;
  rappialiados_marketplace_order_id: any | null;
  requires_checkin: boolean;
  requires_handoff: any | null;
  requires_handshake: any | null;
  retailer_id: string;
  return_allowed: any | null;
  roundtrip: boolean;
  roundtrip_comments: any | null;
  sk_amount: any | null;
  sk_assigned_at: any | null;
  sk_email: any | null;
  sk_first_name: any | null;
  sk_id: any | null;
  sk_last_name: any | null;
  sk_lat: any | null;
  sk_lng: any | null;
  sk_phone: any | null;
  sk_position_updated_at: any | null;
  sk_reassigned: any | null;
  sk_type: any | null;
  sponsored_tip: string;
  timezone_name: any | null;
  total_value: number;
  total_volume: any | null;
  total_weight: any | null;
  tracking_code: string;
  transport_media_type: string | null;
  updated_at: string;
  user_experience_comments: any | null;
  user_experience_rate: any | null;
  user_id: number;
  user_tip: string;
  version: any | null;
  was_cancelled_by_integration: any | null;
  zip_code: any | null;
};

export type Error401Data = {
  globalPermissionFailedActions: string[];
  retailerPermissionFailedActions: string[];
  pickingPointPermissionFailedActions: Array<{
    resource_id: number;
    action: string;
  }>;
};
